
'use client';
import { useState, useEffect, useMemo } from 'react';
import type {
  Query,
  DocumentData,
  QuerySnapshot,
} from 'firebase/firestore';
import { onSnapshot } from 'firebase/firestore';

interface UseCollection<T> {
  data: T[] | null;
  loading: boolean;
  error: Error | null;
}

export function useCollection<T extends DocumentData>(
  query: Query<T> | null
): UseCollection<T> {
  const [data, setData] = useState<T[] | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<Error | null>(null);

  // Extract query info for better error logging
  const queryInfo = useMemo(() => {
    if (!query) return null;
    
    try {
      // Extract collection path from the query
      const path = (query as any)?._query?.path?.segments?.join('/') || 'unknown';
      
      // Try to get filter information if available
      const filters = (query as any)?._query?.filters || [];
      const filterInfo = filters.length > 0 ? `with ${filters.length} filter(s)` : 'no filters';
      
      return `Collection: ${path}, ${filterInfo}`;
    } catch (e) {
      return 'Query info unavailable';
    }
  }, [query]);

  useEffect(() => {
    if (!query) {
      setData(null);
      setLoading(false);
      return;
    }

    setLoading(true);
    let mounted = true;

    const unsubscribe = onSnapshot(
      query,
      (snapshot: QuerySnapshot<T>) => {
        if (!mounted) return;
        
        const result: T[] = [];
        snapshot.forEach((doc) => {
          result.push({ ...doc.data(), id: doc.id } as T);
        });
        
        setData(prevData => {
          // Only update if data has actually changed
          if (JSON.stringify(prevData) === JSON.stringify(result)) {
            return prevData;
          }
          return result;
        });
        setLoading(false);
        setError(null);
      },
      (err) => {
        if (!mounted) return;
        console.error(`Firestore 'useCollection' Error [${queryInfo}]:`, err);
        console.error('Query details:', {
          path: (query as any)?._query?.path?.segments?.join('/'),
          filters: (query as any)?._query?.filters,
          orderBy: (query as any)?._query?.explicitOrderBy,
        });
        setError(err);
        setData(null);
        setLoading(false);
      }
    );

    return () => {
      mounted = false;
      unsubscribe();
    };
  }, [query, queryInfo]);

  return { data, loading, error };
}
